import { IMyFilesGoogleDriveFiles } from './../../my-files/abstract/my-files-google-drive-files';
import { environment } from './../../../../environments/environment.farmer';
import { IMultiPrintDevice } from '@multiprint/abstract/multi-print-device';
import { ICameraPreview } from '@shared/interfaces/camera-preview';
import { Dayjs } from 'dayjs';
import { HttpClient, HttpParams, HttpSentEvent } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IDataOverview } from '@shared/interfaces/admin/data-overview';
import { IMainStats } from '@shared/interfaces/admin/main-stats';
import { IMaterials } from '@shared/interfaces/admin/materials';
import { IReportedProblemDto } from '@shared/interfaces/admin/reported-problem';
import { IDashboardGroup } from '@shared/interfaces/dashboard/dashboard-group';
import { IAddRegisteredDevice } from '@shared/interfaces/device/add-registered-device';
import { IDevice } from '@shared/interfaces/device/device';
import { IDeviceGroupResponse } from '@shared/interfaces/device/device-group-response';
import { IDeviceLog } from '@shared/interfaces/device/device-log';
import { IDeviceShelf } from '@shared/interfaces/device/device-shelf';
import { IDeviceSpecification } from '@shared/interfaces/device/device-specification';
import { IDeviceTeam } from '@shared/interfaces/device/device-team';
import { IDeviceType } from '@shared/interfaces/device/device-type';
import { IRegisteredDevice } from '@shared/interfaces/device/registered-device';
import { IGroup } from '@shared/interfaces/group/group';
import { IInformationAboutNewVersion } from '@shared/interfaces/information-about-new-version';
import { IJobHistory } from '@shared/interfaces/job/job-history';
import { ICurrentPlan } from '@shared/interfaces/landing-page/current-plan';
import { IIncloudBundle } from '@shared/interfaces/landing-page/incloud-bundle';
import { IMaterial } from '@shared/interfaces/material/material';
import { IAvailableDevices } from '@shared/interfaces/multistream/available-devices';
import { IMultiStream } from '@shared/interfaces/multistream/multi-stream';
import { IMultiStreamMesh } from '@shared/interfaces/multistream/multi-stream-mesh';
import { IPageModel } from '@shared/interfaces/page-model';
import { IPagination } from '@shared/interfaces/pagination';
import { ISearchPrintoutList } from '@shared/interfaces/printout/search-printout-list';
import { IRedirectUrl } from '@shared/interfaces/redirect-url';
import { IAddMoreResources } from '@shared/interfaces/resources/add-more-resources';
import { IDevicesResources } from '@shared/interfaces/resources/devices-resources';
import { IStatsAndResources } from '@shared/interfaces/resources/stats-and-resources';
import { ITransferResources } from '@shared/interfaces/resources/transfer-resources';
import { IUsersResources } from '@shared/interfaces/resources/users-resources';
import { IShelf } from '@shared/interfaces/shelf/shelf';
import { IShelfDetails } from '@shared/interfaces/shelf/shelf-details';
import { IShelfUser } from '@shared/interfaces/shelf/shelf-user';
import { IStatistics } from '@shared/interfaces/statistics/statistics';
import { IAvailableTeamDevice } from '@shared/interfaces/team/available-team-device';
import { IAvailableTeamUser } from '@shared/interfaces/team/available-team-user';
import { IManageTeam } from '@shared/interfaces/team/manage-team';
import { ITeam } from '@shared/interfaces/team/team';
import { ITeamDetails } from '@shared/interfaces/team/team-details';
import { ITeamDevice } from '@shared/interfaces/team/team-device';
import { ITeamUser } from '@shared/interfaces/team/team-user';
import { ITermsOfUseContent } from '@shared/interfaces/terms-of-use-content';
import { ITimelineDeviceJob } from '@shared/interfaces/timeline/timeline-device-job';
import { ITimelineGroup } from '@shared/interfaces/timeline/timeline-group';
import { IUserProfile } from '@shared/interfaces/user/user-profile';
import { IUserSubscriptionDetails } from '@shared/interfaces/user/user-subscription-details';
import { ICreateVoucher } from '@shared/interfaces/vouchers/create-voucher';
import { IResellerResources } from '@shared/interfaces/vouchers/reseller-resources';
import { IVouchers } from '@shared/interfaces/vouchers/vouchers';
import { IDevicePrintHistory } from '@shared/interfaces/device/device-print-history';
import { IDeviceTypeFirmware } from '@shared/interfaces/device/device-type-firmware';
import { IAvailableShelfDevice } from '@shared/interfaces/shelf/available-shelf-device';
import { IShelfManage } from '@shared/interfaces/shelf/shelf-manage';
import { IFile } from '@shared/interfaces/file/file';
import { IUploadFileResponse } from '@shared/interfaces/file/upload-file-response';
import { map, Observable } from 'rxjs';
import { IFailureCode, IPrintout } from '@shared/interfaces/printout/printout';
import { IPrintoutFilters } from '@shared/interfaces/printout/printout-filters';
import { IMultiPrintStartPrintingResponse } from '@multiprint/abstract/multi-print-start-printing-response';
import { roles } from '@admin/constants/roles';
import {
  Cacheable,
  GlobalCacheConfig,
  LocalStorageStrategy,
} from 'ts-cacheable';
import {
  adminCacheBuster$,
  specificationCacheBuster$,
  statsAndResourcesCacheBuster$,
  plansCacheBuster$,
} from '@shared/constants/cache-busters';
import { IMyFilesFile } from '@myFiles/abstract/my-files-file';
import { IMyFilesStorage } from '@myFiles/abstract/my-files-storage';
import {
  IStolenDevice,
  IStolenDeviceAdd,
} from '@admin/interfaces/stolen-device.interface';
import { IUserSubscriptionUpdate } from '@shared/interfaces/user/user-subscription-update.interface';
import { ICreatedVoucherResult } from '@shared/interfaces/vouchers/created-voucher-result';
import { IDashboardStatistics } from '@shared/interfaces/statistics/dashboard-statistics.interface';
import { ICreateGoogleFolderDto } from '@shared/interfaces/google/create-google-folder-dto.interface';
import { IShareUser } from '@shared/modules/account/interfaces/share-user.interface';
import { IShareUserDto } from '@shared/modules/account/interfaces/share-user-dto.interface';
import { IPrintUploadPreview } from '@shared/modules/print-file-shared/interfaces/print-upload-preview';
import {
  IAuthRequires2FA,
  IUserAuthorization,
} from '@shared/interfaces/user/user-authorization';
import { IFilesPage } from '@shared/interfaces/file/files-page';
import { IDeviceStatistics } from '../interfaces/device/device-statistics';
import { IDeviceNotificationModel } from '@notifications/abstract/device-notification-model';
import { INotificationAvailableDevice } from '@notifications/abstract/notification-available-device';
import { ITimelineDeviceJobs } from '@shared/interfaces/timeline/timeline-device-jobs';
import { IDeviceJobSchedule } from '@shared/interfaces/device/device-job-schedule';
import {
  IOrganization,
  IOrganizationCountry,
} from '@admin/interfaces/organizations';
import { IDeviceJobSuccessRates } from '@shared/interfaces/statistics/device-job-success-rates';
import { ITwoFactorSetup } from '@shared/interfaces/twoFactorSetup';
import {
  IMonthlyGlobalStatistics,
  ITopDeviceStatistics,
} from '@shared/interfaces/admin/statistics';
import { SalesRegion } from '@shared/enums/sales-region.enum';
import { TopDeviceRanking } from '@admin/components/statistics/top-devices/top-devices.component';
import { IBoughtMaterials } from '@shared/interfaces/statistics/bought-materials';
import { AccessResponse } from 'src/app/modules/access/access-response.model';

GlobalCacheConfig.storageStrategy = LocalStorageStrategy;
GlobalCacheConfig.maxAge = 86400000; //day
GlobalCacheConfig.globalCacheKey = environment.appName;

@Injectable({ providedIn: 'root' })
export class ApiService {
  url: string;

  constructor(private http: HttpClient, @Inject('BASE_URL') baseUrl: string) {
    this.url = baseUrl + (baseUrl.endsWith('/') ? '' : '/');
  }

  confirmEmail(code: string, userId: string, changedEmail: string) {
    var params = new HttpParams().set('code', code).set('userId', userId);
    if (changedEmail) params = params.set('changedEmail', changedEmail);
    return this.http.get<string>(this.url + 'api/auth/confirmEmail', {
      params: params,
    });
  }

  login(data: { email: string; password: string }) {
    return this.http.post<IUserAuthorization | IAuthRequires2FA>(
      this.url + 'api/auth/login',
      data
    );
  }

  register(data: { email: string; password: string }) {
    return this.http.post<any>(this.url + 'api/auth/register', data);
  }

  forgetPassword(data: { email: string }) {
    return this.http.post<boolean>(this.url + 'api/auth/forgotPassword', data);
  }

  resendConfirmationEmail(data: { email: string }) {
    return this.http.post<boolean>(
      this.url + 'api/auth/resendConfirmationEmail',
      data
    );
  }

  resetPassword(data: {
    email: string;
    newPassword: string;
    resetCode: string;
  }) {
    return this.http.post<boolean>(this.url + 'api/auth/resetPassword', data);
  }

  logout() {
    return this.http.post(this.url + 'api/auth/logout', null);
  }

  refreshToken(data: { refreshToken: string }) {
    return this.http.post<IUserAuthorization>(
      this.url + 'api/auth/refresh',
      data
    );
  }

  @Cacheable({ cacheBusterObserver: plansCacheBuster$ })
  getCurrentPlans() {
    return this.http.get<Array<ICurrentPlan>>(
      this.url + 'api/subscription/currentPlans'
    );
  }

  @Cacheable({ cacheBusterObserver: plansCacheBuster$ })
  getAllSubscriptions() {
    return this.http.get<Array<IIncloudBundle>>(this.url + 'api/subscription');
  }

  @Cacheable({ cacheBusterObserver: plansCacheBuster$ })
  getClientCurrency() {
    return this.http.get<{ currency: 'EUR' | 'USD' }>(
      this.url + 'api/profile/currency'
    );
  }

  getDevicesData() {
    return this.http.get<IDeviceGroupResponse[]>(this.url + 'api/device');
  }

  getTransferResources() {
    return this.http.get<ITransferResources>(
      this.url + 'api/subscription/resources/transfer'
    );
  }

  getJobsPrintingByDevices() {
    return this.http.get<IJobHistory[]>(
      this.url + 'api/device/jobsPrintingByDevices'
    );
  }

  getDevicesResources() {
    return this.http.get<IDevicesResources>(
      this.url + 'api/subscription/resources/device'
    );
  }

  getTermOfUseContent(language: string) {
    return this.http.get<ITermsOfUseContent>(
      this.url + 'api/profile/termsOfUseContent/' + language
    );
  }

  getRegisteredDevices() {
    return this.http.get<IRegisteredDevice[]>(
      this.url + 'api/device/registered'
    );
  }

  getMaterialsUsed() {
    return this.http.get<Array<IMaterial>>(
      this.url + 'api/printout/materialsUsed'
    );
  }

  @Cacheable({
    cacheBusterObserver: statsAndResourcesCacheBuster$,
    maxAge: 600000,
  })
  getStatsAndAvailableResources() {
    return this.http.get<IStatsAndResources>(
      this.url + 'api/subscription/resources'
    );
  }

  getUserProfile() {
    return this.http.get<IUserProfile>(this.url + 'api/profile');
  }

  getNotifyAboutNewVersionPrivacyPolicy() {
    return this.http.get(
      this.url + 'api/admin/notifyAboutNewVersionPrivacyPolicy'
    );
  }

  getTeams() {
    return this.http.get<ITeam[]>(this.url + 'api/team');
  }

  getVouchers() {
    return this.http.get<IVouchers>(this.url + 'api/voucher');
  }

  getResellerResources() {
    return this.http.get<IResellerResources>(this.url + 'api/voucher/reseller');
  }

  getResellerEmails() {
    return this.http.get<Array<string>>(
      this.url + 'api/voucher/reseller/emails'
    );
  }

  getDeviceTypesFirmware() {
    return this.http.get<IDeviceTypeFirmware[]>(
      this.url + 'api/settings/firmware'
    );
  }

  getUserResources() {
    return this.http.get<IUsersResources>(
      this.url + 'api/subscription/resources/user'
    );
  }

  getShelves() {
    return this.http.get<IShelf[]>(this.url + 'api/shelf');
  }

  getShelf(id: string) {
    return this.http.get<IShelfDetails>(this.url + 'api/shelf/' + id);
  }

  getAvailableShelfDevices(id: string) {
    return this.http.get<Array<IAvailableShelfDevice>>(
      this.url + 'api/shelf/' + id + '/availableDevices'
    );
  }

  getShelfManage(id: string) {
    return this.http.get<IShelfManage>(
      this.url + 'api/shelf/' + id + '/manage'
    );
  }

  getStatistics(
    daysNumber: number,
    startDate: string | null,
    technology: number = 0
  ) {
    const date = startDate !== null ? startDate : '';
    return this.http.get<IStatistics>(
      `${this.url}api/statistic?technology=${technology}&days=${daysNumber}&startDate=${date}`
    );
  }

  getStatisticsByOrganization(
    organizationId: string,
    daysNumber: number,
    startDate: string | null,
    technology: number = 0
  ) {
    const date = startDate !== null ? startDate : '';
    return this.http.get<IStatistics>(
      `${this.url}api/admin/organization/${organizationId}/statistics?technology=${technology}&days=${daysNumber}&startDate=${date}`
    );
  }

  getDashboardStatistics(technology: number = 0) {
    return this.http.get<IDashboardStatistics>(
      `${this.url}api/statistic/dashboard?technology=${technology}`
    );
  }

  getDownloadStatistics(
    daysNumber: number,
    date: string | null,
    technology: number = 0
  ): Observable<ArrayBuffer> {
    const queryParams = new HttpParams({
      fromObject: {
        technology,
        days: daysNumber,
        startDate: date ?? '',
      },
    });

    return this.http.get(
      `${this.url}api/statistic/download?${queryParams.toString()}`,
      { responseType: 'arraybuffer' }
    );
  }

  getStatisticsForDevice(
    deviceId: string,
    daysNumber: number,
    startDate: string | null
  ) {
    const date = startDate !== null ? startDate : '';
    return this.http.get<IStatistics>(
      `${this.url}api/statistic/device/${deviceId}?days=${daysNumber}&startDate=${date}`
    );
  }

  getJobSuccessRatesStatistics(
    year: number,
    technology: number,
    month?: number,
    deviceId?: string
  ) {
    let query = `${this.url}api/statistic/jobSuccessRates?year=${year}&technology=${technology}`;
    if (month) query += '&month=' + month;
    if (deviceId) query += '&deviceId=' + deviceId;

    return this.http.get<IDeviceJobSuccessRates[]>(`${query}`);
  }

  getGetBoughtMaterialsStatistics(year?: number | null, month?: number) {
    let query = `${this.url}api/statistic/bought-materials`;
    if (year) query += '?year=' + year;
    if (month) query += '&month=' + month;

    return this.http.get<IBoughtMaterials>(`${query}`);
  }

  getDownloadStatisticsForDevice(
    deviceId: string,
    daysNumber: number,
    date: string | null
  ): Observable<ArrayBuffer> {
    const queryParams = new HttpParams({
      fromObject: {
        days: daysNumber,
        startDate: date ?? '',
      },
    });

    return this.http.get(
      `${
        this.url
      }api/statistic/device/${deviceId}/download?${queryParams.toString()}`,
      { responseType: 'arraybuffer' }
    );
  }

  getConnectedDevices() {
    return this.http.get(this.url + 'api/admin/connectedDevices', {
      responseType: 'arraybuffer',
    });
  }

  getUsersSubscription(
    maxRecords: number,
    pageNumber: number,
    searchString: string
  ) {
    return this.http.get<IPageModel<IUserSubscriptionDetails>>(
      this.url +
        'api/admin/userSubscriptions?maxRecords=' +
        maxRecords +
        '&pageNumber=' +
        pageNumber +
        '&searchString=' +
        searchString
    );
  }

  getVouchersSummary() {
    return this.http.get(this.url + 'api/admin/exportVouchers', {
      responseType: 'arraybuffer',
    });
  }

  getResellerVouchers(resellerId: string) {
    return this.http.get(
      `${this.url}api/admin/exportVouchers/resellers/${resellerId}`,
      { responseType: 'arraybuffer' }
    );
  }

  getUserVouchers(userId: string) {
    return this.http.get(`${this.url}api/admin/exportVouchers/user/${userId}`, {
      responseType: 'arraybuffer',
    });
  }

  getResellerSummary() {
    return this.http.get(`${this.url}api/admin/exportResellers`, {
      responseType: 'arraybuffer',
    });
  }

  getDataOverview(days: number) {
    return this.http.get<IDataOverview>(
      this.url + 'api/admin/dataOverview?days=' + days
    );
  }

  getMainStats() {
    return this.http.get<IMainStats>(this.url + 'api/admin/mainStats');
  }

  @Cacheable({ cacheBusterObserver: adminCacheBuster$ })
  getAllDeviceTypes() {
    return this.http.get<IDeviceType[]>(this.url + 'api/admin/deviceTypes');
  }

  getReportedProblems() {
    return this.http.get<IReportedProblemDto[]>(
      this.url + 'api/admin/reportedProblems'
    );
  }

  getDashboardGroups(groupingEnabled: boolean) {
    return this.http.get<Array<IDashboardGroup>>(
      this.url + 'api/group/dashboard?grouping=' + groupingEnabled
    );
  }

  getAvailableDevicesInMultiStream() {
    return this.http.get<IAvailableDevices[]>(
      this.url + 'api/multiStream/availableDevices'
    );
  }

  getMultiStream() {
    return this.http.get<Array<IMultiStream>>(this.url + 'api/multiStream');
  }

  getMultiStreamMesh(deviceId: string) {
    return this.http.get<IMultiStreamMesh>(
      this.url + 'api/multiStream/mesh?deviceId=' + deviceId
    );
  }

  getDevice(deviceId: string) {
    return this.http.get<IDevice>(this.url + 'api/device/' + deviceId);
  }

  getDeviceTeams(deviceId: string) {
    return this.http.get<Array<IDeviceTeam>>(
      this.url + 'api/device/' + deviceId + '/teams'
    );
  }

  getDeviceShelves(deviceId: string) {
    return this.http.get<Array<IDeviceShelf>>(
      this.url + 'api/device/' + deviceId + '/shelves'
    );
  }

  @Cacheable({ cacheBusterObserver: specificationCacheBuster$ })
  getDeviceSpecification(deviceTypeId: string) {
    return this.http.get<IDeviceSpecification>(
      this.url + 'api/device/' + deviceTypeId + '/specification'
    );
  }

  getDeviceLogs(deviceId: string, maxRecords: number, pageNumber: number) {
    return this.http.get<IPageModel<IDeviceLog>>(
      this.url +
        'api/device/' +
        deviceId +
        '/logs?maxRecords=' +
        maxRecords +
        '&pageNumber=' +
        pageNumber
    );
  }

  getGroups() {
    return this.http.get<Array<IGroup>>(this.url + 'api/group');
  }

  getDeviceGroup(deviceId: string) {
    return this.http.get<IGroup>(this.url + 'api/group/device/' + deviceId);
  }

  getTeamDetails(teamId: string) {
    return this.http.get<ITeamDetails>(
      this.url + 'api/team/' + teamId + '/details'
    );
  }

  getShelfDetails(shelfId: string) {
    return this.http.get<IShelfDetails>(
      this.url + 'api/shelf/' + shelfId + '/details'
    );
  }

  postReportProblem(data: FormData) {
    return this.http.post(this.url + 'api/profile/reportedProblem', data);
  }

  getCloudNewVersionInformation(version: string) {
    return this.http.get<Array<string>>(
      this.url + 'api/profile/cloudNewVersionInformation/' + version
    );
  }

  getAvailableTeamUsers(teamId?: string | null) {
    const url =
      teamId !== null
        ? 'api/team/availableUsers?teamId=' + teamId
        : 'api/team/availableUsers';
    return this.http.get<Array<IAvailableTeamUser>>(this.url + url);
  }

  getTimelineDevices(isGrouping: boolean) {
    return this.http.get<Array<ITimelineGroup>>(
      this.url + 'api/timeline/devices?grouping=' + isGrouping
    );
  }

  getTimelineJobsFromRange(fromDate: Dayjs, toDate: Dayjs) {
    return this.http.get<ITimelineDeviceJobs>(
      this.url +
        'api/timeline/jobs?fromDate=' +
        fromDate.toISOString() +
        '&toDate=' +
        toDate.toISOString()
    );
  }

  getDevicePrintHistory(
    deviceId: string,
    maxRecords: number,
    pageNumber: number
  ) {
    return this.http.get<IPageModel<IDevicePrintHistory>>(
      `${this.url}api/device/${deviceId}/printHistory?pageNumber=${pageNumber}&maxRecords=${maxRecords}`
    );
  }

  getDeviceFiles(
    deviceId: string,
    maxRecords: number,
    pageNumber: number,
    path: string = '/',
    searchString: string = '',
    matchingToDevice: boolean = false
  ) {
    return this.http.get<IFilesPage>(
      `${this.url}api/file/${deviceId}/files?pageNumber=${pageNumber}&maxRecords=${maxRecords}&searchString=${searchString}&path=${path}&matchingToDevice=${matchingToDevice}`
    );
  }

  @Cacheable({ cacheBusterObserver: adminCacheBuster$ })
  getMaterials(pageNumber: number, maxRecords: number) {
    return this.http.get<IPageModel<IMaterial>>(
      this.url +
        'api/admin/materials?maxRecords=' +
        maxRecords +
        '&pageNumber=' +
        pageNumber
    );
  }

  @Cacheable({ cacheBusterObserver: adminCacheBuster$, maxAge: 60000 })
  getOrganizations(pageNumber: number, maxRecords: number) {
    return this.http.get<IPageModel<IOrganization>>(
      this.url +
        'api/admin/organizations?maxRecords=' +
        maxRecords +
        '&pageNumber=' +
        pageNumber
    );
  }

  @Cacheable({ cacheBusterObserver: adminCacheBuster$, maxAge: 60000 })
  getOrganizationsCountries() {
    return this.http.get<IOrganizationCountry[]>(
      this.url + 'api/admin/organizations/countries'
    );
  }

  @Cacheable({ cacheBusterObserver: adminCacheBuster$, maxAge: 60000 })
  getOrganizationsByCountry(country: string) {
    return this.http.get<IOrganization[]>(
      this.url + `api/admin/organizations/countries/${country}`
    );
  }

  getCameraPreview(deviceId: string) {
    return this.http
      .get<ICameraPreview>(this.url + 'api/camera/' + deviceId)
      .pipe(
        map((p) => {
          p.deviceId = deviceId;
          return p;
        })
      );
  }

  getPrintoutFilters() {
    return this.http.get<IPrintoutFilters>(this.url + 'api/printout/filters');
  }

  getSearchPrintouts(data: ISearchPrintoutList) {
    let query =
      'api/printout?maxRecords=' +
      data.maxRecord +
      '&pageNumber=' +
      data.pageNumber;

    if (data.startDate) query += '&startDate=' + data.startDate;
    if (data.endDate) query += '&endDate=' + data.endDate;
    if (data.specificDate) query += '&specificDate=' + data.specificDate;
    if (data.deviceId) query += '&deviceId=' + data.deviceId;
    if (data.deviceTypeId) query += '&deviceTypeId=' + data.deviceTypeId;
    if (data.externalMaterialId)
      query += '&externalMaterialId=' + data.externalMaterialId;
    if (data.result !== null) query += '&result=' + data.result;
    if (data.userRole !== null) query += '&userRole=' + data.userRole;

    return this.http.get<IPageModel<IPrintout>>(this.url + query);
  }

  getMultiPrintDevicesByType(type: number) {
    return this.http.get<IMultiPrintDevice[]>(
      this.url + 'api/multiPrint?type=' + type
    );
  }

  getGoogleDriveFiles(
    parentId: string = '',
    nextPageToken: string = '',
    orderBy: number = 0,
    direction = 'asc',
    searchPattern: string
  ) {
    const urlParams = new URLSearchParams({
      direction,
      orderBy: orderBy.toString(),
    });

    parentId && urlParams.set('parentId', parentId);
    nextPageToken && urlParams.set('nextPageToken', nextPageToken);
    searchPattern && urlParams.set('searchPattern', searchPattern);

    return this.http.get<IMyFilesGoogleDriveFiles>(
      `${this.url}api/googleDrive?${urlParams.toString()}`
    );
  }

  getGoogleDriveSharedFiles(
    parentId: string = '',
    nextPageToken: string = '',
    orderBy: number = 0,
    direction = 'asc',
    searchPattern: string
  ) {
    const urlParams = new URLSearchParams({
      direction,
      orderBy: orderBy.toString(),
    });

    parentId && urlParams.set('parentId', parentId);
    nextPageToken && urlParams.set('nextPageToken', nextPageToken);
    searchPattern && urlParams.set('searchPattern', searchPattern);

    return this.http.get<IMyFilesGoogleDriveFiles>(
      `${this.url}api/googleDrive/shared?${urlParams.toString()}`
    );
  }

  getGoogleDriveFileById(id: string) {
    return this.http.get<IMyFilesFile>(this.url + `api/googleDrive/${id}`);
  }

  getGoogleDriveFilePermissionsById(id: string) {
    return this.http.get<IShareUserDto>(
      this.url + `api/googleDrive/${id}/permissions`
    );
  }

  getGoogleDriveStorage() {
    return this.http.get<IMyFilesStorage>(this.url + `api/googleDrive/storage`);
  }

  getGoogleDriveDownloadFile(id: string) {
    return this.http.get(this.url + `api/googleDrive/download/${id}`, {
      responseType: 'arraybuffer',
    });
  }

  getStolenDevices(): Observable<IStolenDevice[]> {
    return this.http.get<IStolenDevice[]>(this.url + 'api/admin/stolenDevices');
  }

  patchMoveGoogleDriveFile(fileId: string, folderId: string): Observable<void> {
    return this.http.patch<void>(`${this.url}api/googleDrive/move/${fileId}`, {
      folderId,
    });
  }

  patchAcceptPrivacyPolicy() {
    const body = [
      {
        op: 'replace',
        path: '/privacyPolicyDelivered',
        value: true,
      },
    ];

    return this.http.patch(this.url + 'api/profile', body);
  }

  patchcConfirmNewVersion() {
    const body = [
      {
        op: 'replace',
        path: '/newVersionDelivered',
        value: true,
      },
    ];

    return this.http.patch(this.url + 'api/profile', body);
  }

  patchUpdateDevice(
    deviceId: string,
    deviceName: string,
    deviceDescription: string
  ) {
    const body = [
      {
        op: 'replace',
        path: '/deviceName',
        value: deviceName,
      },
      {
        op: 'replace',
        path: '/deviceDescription',
        value: deviceDescription,
      },
    ];

    return this.http.patch(this.url + 'api/device/' + deviceId, body);
  }

  postAddResources(data: IAddMoreResources) {
    return this.http.post<IRedirectUrl>(
      this.url + 'api/subscription/resources/add',
      data
    );
  }

  postAddRegisteredDevice(data: IAddRegisteredDevice) {
    return this.http.post<IDevice>(this.url + 'api/device', data);
  }

  postAddDeviceToGroup(data: { deviceId: string; groupId: string }) {
    return this.http.post(this.url + 'api/group/addDevice', data);
  }

  postCreateGroup(data: { name: string }) {
    return this.http.post<IDashboardGroup>(this.url + 'api/group', data);
  }

  postStartMultiPrint(uploadId: string, deviceIds: string[]) {
    return this.http.post<IMultiPrintStartPrintingResponse[]>(
      this.url + 'api/multiprint/start',
      { uploadId, deviceIds }
    );
  }

  postStopMultiPrint(deviceIds: string[]) {
    return this.http.post(this.url + 'api/multiprint/stop', { deviceIds });
  }

  postTeamDetails(data: { teamId: string }) {
    return this.http.post<ITeamDetails>(
      this.url + 'api/team/teamDetails',
      data
    );
  }

  postStartPrinting(deviceId: string, path: string) {
    return this.http.post(`${this.url}api/command/${deviceId}/start`, { path });
  }

  postStopPrinting(deviceId: string) {
    return this.http.post(`${this.url}api/command/${deviceId}/stop`, {});
  }

  postResumePrinting(deviceId: string) {
    return this.http.post(`${this.url}api/command/${deviceId}/resume`, {});
  }

  postPausePrinting(deviceId: string) {
    return this.http.post(`${this.url}api/command/${deviceId}/pause`, {});
  }

  postStartUpdatingDevice(deviceId: string) {
    return this.http.post(`${this.url}api/command/${deviceId}/update`, {});
  }

  postInformationAboutNewVersion(data: IInformationAboutNewVersion) {
    return this.http.post(
      this.url + 'api/admin/cloudNewVersionInformation',
      data
    );
  }

  postSendVoucher(data: { code: string }) {
    return this.http.post(this.url + 'api/voucher/send', data);
  }

  postCreateVoucher(data: ICreateVoucher) {
    return this.http.post<ICreatedVoucherResult>(
      this.url + 'api/voucher',
      data
    );
  }

  postCreateResellerResources(data: IResellerResources) {
    return this.http.post(this.url + 'api/voucher/reseller', data);
  }

  postDownloadVoucherPdf(code: string) {
    return this.http.get(this.url + 'api/voucher/download/' + code, {
      responseType: 'arraybuffer',
    });
  }

  postUseVoucher(data: { code: string }) {
    return this.http.post(this.url + 'api/voucher/use', data);
  }

  postCreateTeam(data: { name: string }) {
    return this.http.post<ITeam>(this.url + 'api/team', data);
  }

  postDeviceFindMethod(deviceId: string, active: boolean) {
    return this.http.post(`${this.url}api/command/${deviceId}/find`, {
      active,
    });
  }

  postCameraStart(deviceId: string) {
    return this.http.post<{ id: string }>(
      `${this.url}api/camera/${deviceId}/start`,
      null
    );
  }

  postCameraStop(deviceId: string, streamId: string) {
    return this.http.post(`${this.url}api/camera/${deviceId}/stop`, {
      id: streamId,
    });
  }

  postCameraRefresh(deviceId: string, streamId: string) {
    return this.http.post(`${this.url}api/camera/${deviceId}/refresh`, {
      id: streamId,
    });
  }

  postAddUserToTeam(teamId: string, userEmail: string) {
    return this.http.post<ITeamUser>(
      this.url + 'api/team/' + teamId + '/user',
      { teamId: teamId, userEmail: userEmail }
    );
  }

  postAddDeviceToTeam(teamId: string, deviceId: string) {
    return this.http.post<ITeamDevice>(
      this.url + 'api/team/' + teamId + '/device',
      { teamId: teamId, deviceId: deviceId }
    );
  }

  postAddNewCatalog(deviceId: string, path: string) {
    return this.http.post<IFile>(this.url + `api/file/${deviceId}/files`, {
      fullPath: path,
    });
  }

  postCreateShelf(data: {
    name: string;
    columnCount: number;
    rowCount: number;
  }) {
    return this.http.post<IShelf>(this.url + 'api/shelf', data);
  }

  postAddDeviceToShelf(data: {
    shelfId: string;
    columnNumber: number;
    rowNumber: number;
    deviceId: string;
  }) {
    return this.http.post(
      this.url + 'api/shelf/' + data.shelfId + '/device',
      data
    );
  }

  postAddUserToShelf(shelfId: string, userEmail: string) {
    return this.http.post<IShelfUser>(
      this.url + 'api/shelf/' + shelfId + '/user',
      { shelfId: shelfId, userEmail: userEmail }
    );
  }

  postAllMaterials(data: IPagination) {
    return this.http.post<IMaterials>(
      this.url + 'api/admin/allMaterials',
      data
    );
  }

  postAddDeviceType(data: IDeviceType) {
    return this.http.post(this.url + 'api/admin/deviceType', data);
  }

  postAddMaterial(data: {
    materialName: string;
    materialType: number;
    externalId: number;
    shortName: string;
  }) {
    return this.http.post(this.url + 'api/admin/material', data);
  }

  postFileUploadPreview(data: FormData) {
    return this.http.post<IPrintUploadPreview>(
      this.url + 'api/multiprint/upload',
      data,
      {
        reportProgress: true,
        observe: 'events',
      }
    );
  }

  postFileUpload(data: FormData, deviceId: string) {
    return this.http.post<IUploadFileResponse>(
      `${this.url}api/file/${deviceId}/files/upload`,
      data,
      {
        reportProgress: true,
        observe: 'events',
      }
    );
  }

  postFileUploadToPrintout(jobId: string, data: FormData) {
    return this.http.post<IUploadFileResponse>(
      `${this.url}api/printout/${jobId}/addAttachment`,
      data,
      {
        reportProgress: true,
        observe: 'events',
      }
    );
  }

  postCancelUpload(deviceId: string, uploadId: string) {
    return this.http.post(
      `${this.url}api/file/${deviceId}/files/cancelUpload`,
      { uploadId }
    );
  }

  postAddDeviceToMultiStream(data: { deviceId: string }) {
    return this.http.post<IAvailableDevices>(
      this.url + 'api/multiStream',
      data
    );
  }

  postCheckIfDeviceStreamExist(deviceId: string, streamId: string) {
    return this.http.post(
      `${this.url}api/camera/${deviceId}/checkIfStreamExist`,
      { id: streamId }
    );
  }

  postManageTeam(data: IManageTeam) {
    return this.http.post(
      this.url + 'api/team/' + data.teamId + '/manage',
      data
    );
  }

  postTermsOfUse(data: {
    content: any;
    language: string;
    notifyAboutNewVersion: boolean;
  }) {
    return this.http.post(this.url + 'api/admin/termsOfUse', data);
  }

  postStolenDevices(data: IStolenDeviceAdd): Observable<IStolenDevice> {
    return this.http.post<IStolenDevice>(
      this.url + 'api/admin/stolenDevices',
      data
    );
  }

  postGoogleDriveFileDirect(fileId: string): Observable<IPrintUploadPreview> {
    return this.http.post<IPrintUploadPreview>(
      `${this.url}api/googleDrive/uploadFileDirect`,
      { fileId }
    );
  }

  postCreateGoogleFolder(
    data: ICreateGoogleFolderDto
  ): Observable<IMyFilesFile> {
    return this.http.post<IMyFilesFile>(
      `${this.url}api/googleDrive/folder`,
      data
    );
  }

  postCreateGooglePermission(
    fileId: string,
    email: string
  ): Observable<IShareUser> {
    return this.http.post<IShareUser>(
      `${this.url}api/googleDrive/${fileId}/permissions`,
      { email }
    );
  }

  postUploadGoogleDriveFile(data: FormData) {
    return this.http.post<void>(`${this.url}api/googleDrive/upload`, data, {
      reportProgress: true,
      observe: 'events',
    });
  }

  putEditShelf(
    id: string,
    data: { name: string; columnCount: number; rowCount: number }
  ) {
    return this.http.put<IShelfDetails>(this.url + 'api/shelf/' + id, data);
  }

  putGroupUpdate(groupId: string, groupName: string) {
    return this.http.put(this.url + 'api/group/' + groupId, {
      name: groupName,
    });
  }

  putRatingChange(data: {
    deviceId: string;
    jobId: string;
    rating: number;
    failureCode: IFailureCode;
  }) {
    return this.http.put(this.url + 'api/printout/rating', data);
  }

  putEditDeviceType(deviceTypeId: string, data: IDeviceType) {
    return this.http.put(
      this.url + 'api/admin/deviceType/' + deviceTypeId,
      data
    );
  }

  putEditUserRoles(userId: string, newRoles: roles[]) {
    return this.http.put(this.url + 'api/admin/user/roles', {
      id: userId,
      roles: newRoles,
    });
  }

  putEditMaterial(
    materialId: string,
    data: {
      materialName: string;
      materialType: number;
      shortName: string;
      externalId: number;
    }
  ) {
    return this.http.put(this.url + 'api/admin/material/' + materialId, data);
  }

  putUserGroupDevice(data: {
    oldGroupId: string;
    newGroupId: string;
    deviceId: string;
  }) {
    return this.http.put(this.url + 'api/group/userGroupDevice', data);
  }

  putUserSubscription(userEmail: string, data: IUserSubscriptionUpdate) {
    return this.http.put(
      this.url + 'api/admin/userSubscription/' + userEmail,
      data
    );
  }

  putEditTeam(teamId: string, name: string) {
    return this.http.put(this.url + 'api/team/' + teamId, { name: name });
  }

  deleteDeviceFromGroup(data: { body: { deviceId: string; groupId: string } }) {
    return this.http.delete(this.url + 'api/group/deleteDevice', data);
  }

  deleteDevice(deviceId: string) {
    return this.http.delete(this.url + 'api/device/' + deviceId);
  }

  deleteDeviceFile(deviceId: string, id: string) {
    return this.http.delete(`${this.url}api/file/${deviceId}/files/${id}`);
  }

  deleteUserFromTeam(teamId: string, userId: string) {
    return this.http.delete(
      this.url + 'api/team/' + teamId + '/user/' + userId
    );
  }

  deleteTeam(teamId: string) {
    return this.http.delete(this.url + 'api/team/' + teamId);
  }

  deleteDeviceFromTeam(teamId: string, deviceId: string) {
    return this.http.delete<IAvailableTeamDevice>(
      this.url + 'api/team/' + teamId + '/device/' + deviceId
    );
  }

  deleteReportedProblem(reportedProblemId: string) {
    return this.http.delete(
      this.url + 'api/admin/reportedProblem/' + reportedProblemId
    );
  }

  deleteMaterial(id: string) {
    return this.http.delete(this.url + 'api/admin/material/' + id);
  }

  deleteDeviceType(deviceTypeId: string) {
    return this.http.delete(this.url + 'api/admin/deviceType/' + deviceTypeId);
  }

  deleteUserAvatar() {
    return this.http.delete(this.url + 'api/profile/deleteAvatar');
  }

  deleteDeviceFromShelf(deviceId: string, shelfId: string) {
    return this.http.delete(
      this.url + 'api/shelf/' + shelfId + '/device/' + deviceId
    );
  }

  deleteShelf(id: string) {
    return this.http.delete(this.url + 'api/shelf/' + id);
  }

  deleteUserLeaveTeam(teamId: string) {
    return this.http.delete(this.url + 'api/team/' + teamId + '/leave');
  }

  deleteUserLeaveShelf(shelfId: string) {
    return this.http.delete(this.url + 'api/shelf/' + shelfId + '/leave');
  }

  deleteDeviceFromMultiStream(id: string) {
    return this.http.delete(`${this.url}api/multiStream/${id}`);
  }

  deleteVoucher(id: number) {
    return this.http.delete(this.url + 'api/voucher/' + id);
  }

  deleteGroup(groupId: string, grouping: boolean) {
    return this.http.delete<Array<IDashboardGroup>>(
      this.url + 'api/group/' + groupId,
      { body: grouping }
    );
  }

  deleteShelfUser(userId: string, shelfId: string) {
    return this.http.delete(
      this.url + 'api/shelf/' + shelfId + '/user/' + userId
    );
  }

  deleteStolenDevice(serialNumber: string): Observable<void> {
    return this.http.delete<void>(
      this.url + `api/admin/stolenDevices/${serialNumber}`
    );
  }

  deleteGoogleDriveFile(id: string): Observable<void> {
    return this.http.delete<void>(this.url + `api/googleDrive/${id}`);
  }

  deleteGoogleDriveFilePermission(fileId: string, permissionId: string) {
    return this.http.delete<void>(
      this.url + `api/googleDrive/${fileId}/permissions/${permissionId}`
    );
  }

  //device notifications
  getUnreadUserDeviceNotificationsCount() {
    return this.http.get<number>(
      this.url + `api/userDeviceNotification/unreadCount`
    );
  }

  getUserDeviceNotifications(
    maxRecords: number,
    pageNumber: number,
    deviceId: string | null
  ) {
    let query =
      'api/userDeviceNotification?pageNumber=' +
      pageNumber +
      '&maxRecords=' +
      maxRecords;

    if (deviceId) query += '&deviceId=' + deviceId;
    return this.http.get<IPageModel<IDeviceNotificationModel>>(
      this.url + query
    );
  }

  getUserNotificationAvailableDevices() {
    return this.http.get<INotificationAvailableDevice[]>(
      this.url + `api/userDeviceNotification/availableDevices`
    );
  }

  markAsReadUserDeviceNotifications(ids: number[]) {
    return this.http.post<void>(
      this.url + `api/userDeviceNotification/markAsRead`,
      { ids }
    );
  }

  deleteUserDeviceNotifications(ids: number[]) {
    return this.http.delete<void>(this.url + `api/userDeviceNotification`, {
      body: ids,
    });
  }

  validateScheduleJob(deviceId: string, startDate: Date, endDate: Date) {
    return this.http.post(
      this.url + `api/schedulerJobs/device/${deviceId}/schedule/validate`,
      {
        startDate,
        endDate,
      }
    );
  }

  scheduleJob(deviceId: string, startDate: Date, filePath: string) {
    return this.http.post(
      this.url + `api/schedulerJobs/device/${deviceId}/schedule`,
      {
        startDate,
        filePath,
      }
    );
  }

  cancelScheduleJob(deviceId: string, id: number) {
    return this.http.post(
      this.url + `api/schedulerJobs/device/${deviceId}/schedule/${id}/cancel`,
      {}
    );
  }

  getDeviceScheduledJobs(
    deviceId: string,
    maxRecords: number,
    pageNumber: number
  ) {
    return this.http.get<IPageModel<IDeviceJobSchedule>>(
      `${this.url}api/schedulerJobs/device/${deviceId}?pageNumber=${pageNumber}&maxRecords=${maxRecords}`
    );
  }

  //2fa
  setupTwoFactorAuth() {
    return this.http.post<ITwoFactorSetup>(`${this.url}api/auth/2fa/setup`, {});
  }

  verifyTwoFactorAuth(code: string) {
    return this.http.post<ITwoFactorSetup>(`${this.url}api/auth/2fa/verify`, {
      code,
    });
  }

  loginTwoFactor(data: { twoFactorToken: string; code: string }) {
    return this.http.post<IUserAuthorization>(
      `${this.url}api/auth/2fa/login`,
      data
    );
  }

  //admin statistics
  getGlobalMonthlyStatistics(year: number) {
    return this.http.get<IMonthlyGlobalStatistics[]>(
      `${this.url}api/admin/statistics/monthly/global?year=${year}`
    );
  }

  getMonthlyStatisticsByDeviceTypeAndRegion(
    year: number,
    deviceTypeId: string,
    region: SalesRegion
  ) {
    return this.http.get<IMonthlyGlobalStatistics[]>(
      `${this.url}api/admin/statistics/monthly/device-type?year=${year}&deviceTypeId=${deviceTypeId}&region=${region}`
    );
  }

  getTopDevicesByRanking(
    ranking: TopDeviceRanking
  ): Observable<ITopDeviceStatistics[]> {
    const endpoint =
      ranking === TopDeviceRanking.BySuccessRate
        ? 'api/admin/statistics/top-devices/success-rate'
        : 'api/admin/statistics/top-devices/utilization-rate';
    return this.http.get<ITopDeviceStatistics[]>(this.url + endpoint);
  }

  getMe() {
    return this.http.get<AccessResponse>(`${this.url}api/me/access`);
  }

  createPreviewToken(userId: string, organizationId: string) {
    return this.http.post<IUserAuthorization>(
      this.url +
        `api/admin/organizations/${organizationId}/users/${userId}/preview-token`,
      {}
    );
  }
}
